'use client';

import { useCallback } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { NovelReadingOptions } from '@/utils/novel-reading-options';

interface ChapterTextContentProps {
    tipo: 'html' | 'markdown' | 'texto';
    conteudo: string;
    zoom?: number;
    brightness?: number;
    saturation?: number;
    /** Opções de leitura (novel): fonte, tamanho, espaçamento, background */
    readingOptions?: NovelReadingOptions | null;
}

export default function ChapterTextContent({
    tipo,
    conteudo,
    zoom = 100,
    brightness = 100,
    saturation = 100,
    readingOptions,
}: ChapterTextContentProps) {
    const urlTransform = useCallback((url: string) => {
        return url.startsWith('data:') ? url : defaultUrlTransform(url);
    }, []);

    const containerStyle: React.CSSProperties = {
        fontSize: `${zoom}%`,
        filter: `brightness(${brightness}%) saturate(${saturation}%)`,
    };

    const wrapperStyle: React.CSSProperties = {
        padding: '2rem',
        borderRadius: '8px',
        margin: '1rem 0',
    };
    if (readingOptions?.background && readingOptions.background !== 'transparent') {
        wrapperStyle.background = readingOptions.background;
        wrapperStyle.color = '#1a1a1a';
    }
    // Fonte, tamanho e espaçamento no wrapper para todo o texto herdar (evita prose/Tailwind sobrescrever)
    if (readingOptions) {
        if (readingOptions.fontSize) wrapperStyle.fontSize = `${readingOptions.fontSize}px`;
        if (readingOptions.lineHeight) wrapperStyle.lineHeight = readingOptions.lineHeight / 100;
        if (readingOptions.font) wrapperStyle.fontFamily = readingOptions.font;
        containerStyle.fontSize = '1em';
        containerStyle.lineHeight = 'inherit';
        containerStyle.fontFamily = 'inherit';
    }

    const hasReadingOptions = Boolean(readingOptions?.font || readingOptions?.fontSize || readingOptions?.lineHeight);

    if (tipo === 'html') {
        return (
            <div
                className={`chapter-html-content ${hasReadingOptions ? 'novel-reading-options' : ''}`}
                style={wrapperStyle}
            >
                <div
                    className="prose prose-invert max-w-none"
                    style={containerStyle}
                    dangerouslySetInnerHTML={{ __html: conteudo }}
                />
                <style dangerouslySetInnerHTML={{
                    __html: `
                    .chapter-html-content {
                        border-radius: 8px;
                    }
                    .chapter-html-content .prose {
                        color: inherit;
                    }
                    .chapter-html-content .prose p,
                    .chapter-html-content .prose li,
                    .chapter-html-content .prose blockquote {
                        color: inherit;
                    }
                    .chapter-html-content .prose h1, .chapter-html-content .prose h2,
                    .chapter-html-content .prose h3, .chapter-html-content .prose h4,
                    .chapter-html-content .prose h5, .chapter-html-content .prose h6 {
                        color: inherit;
                    }
                    .chapter-html-content.novel-reading-options .prose,
                    .chapter-html-content.novel-reading-options .prose p,
                    .chapter-html-content.novel-reading-options .prose li,
                    .chapter-html-content.novel-reading-options .prose span,
                    .chapter-html-content.novel-reading-options .prose div,
                    .chapter-html-content.novel-reading-options .prose blockquote {
                        font-size: inherit !important;
                        font-family: inherit !important;
                        line-height: inherit !important;
                    }
                    .chapter-html-content.novel-reading-options .prose h1 { font-size: 1.4em !important; }
                    .chapter-html-content.novel-reading-options .prose h2 { font-size: 1.25em !important; }
                    .chapter-html-content.novel-reading-options .prose h3 { font-size: 1.15em !important; }
                    .chapter-html-content.novel-reading-options .prose h4,
                    .chapter-html-content.novel-reading-options .prose h5,
                    .chapter-html-content.novel-reading-options .prose h6 { font-size: 1.05em !important; }
                    
                    .prose {
                        line-height: inherit;
                    }
                    
                    .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
                        color: #ffffff;
                        margin-top: 2rem;
                        margin-bottom: 1rem;
                        font-weight: 700;
                        line-height: 1.3;
                    }

                    .prose h1 { font-size: 2.25em; }
                    .prose h2 { font-size: 1.875em; }
                    .prose h3 { font-size: 1.5em; }
                    .prose h4 { font-size: 1.25em; }
                    .prose h5 { font-size: 1.125em; }
                    .prose h6 { font-size: 1em; }
                    
                    .prose p {
                        margin-bottom: 1.5rem;
                        text-align: justify;
                    }
                    
                    .prose a {
                        color: #60a5fa;
                        text-decoration: underline;
                    }
                    
                    .prose code {
                        background: #2d2d2d;
                        padding: 0.2rem 0.4rem;
                        border-radius: 4px;
                        font-size: 0.9em;
                    }
                    
                    .prose pre {
                        background: #2d2d2d;
                        padding: 1rem;
                        border-radius: 8px;
                        overflow-x: auto;
                    }
                    
                    .prose blockquote {
                        border-left: 4px solid #60a5fa;
                        padding-left: 1rem;
                        font-style: italic;
                        color: #b0b0b0;
                    }
                    
                    .prose img {
                        max-width: 100%;
                        height: auto;
                        border-radius: 8px;
                        margin: 1.5rem auto;
                        display: block;
                    }
                    
                    .prose ul, .prose ol {
                        margin-left: 1.5rem;
                        margin-bottom: 1.5rem;
                    }
                    
                    .prose li {
                        margin-bottom: 0.5rem;
                    }
                ` }} />
            </div>
        );
    }

    if (tipo === 'markdown') {
        return (
            <div
                className={`chapter-markdown-content ${hasReadingOptions ? 'novel-reading-options' : ''}`}
                style={wrapperStyle}
            >
                <div className="prose prose-invert max-w-none" style={containerStyle}>
                    <ReactMarkdown
                        urlTransform={urlTransform}
                        remarkPlugins={[remarkGfm]}
                    // rehypePlugins={[rehypeRaw,rehypeSanitize]} esse plugin mata tudo
                    >
                        {conteudo}
                    </ReactMarkdown>
                </div>
                <style dangerouslySetInnerHTML={{
                    __html: `
                    .chapter-markdown-content {
                        border-radius: 8px;
                    }
                    .chapter-markdown-content .prose {
                        color: inherit;
                    }
                    .chapter-markdown-content .prose p,
                    .chapter-markdown-content .prose li,
                    .chapter-markdown-content .prose blockquote {
                        color: inherit;
                    }
                    .chapter-markdown-content .prose h1, .chapter-markdown-content .prose h2,
                    .chapter-markdown-content .prose h3, .chapter-markdown-content .prose h4,
                    .chapter-markdown-content .prose h5, .chapter-markdown-content .prose h6 {
                        color: inherit;
                    }
                    .chapter-markdown-content.novel-reading-options .prose,
                    .chapter-markdown-content.novel-reading-options .prose p,
                    .chapter-markdown-content.novel-reading-options .prose li,
                    .chapter-markdown-content.novel-reading-options .prose span,
                    .chapter-markdown-content.novel-reading-options .prose div,
                    .chapter-markdown-content.novel-reading-options .prose blockquote {
                        font-size: inherit !important;
                        font-family: inherit !important;
                        line-height: inherit !important;
                    }
                    .chapter-markdown-content.novel-reading-options .prose h1 { font-size: 1.4em !important; }
                    .chapter-markdown-content.novel-reading-options .prose h2 { font-size: 1.25em !important; }
                    .chapter-markdown-content.novel-reading-options .prose h3 { font-size: 1.15em !important; }
                    .chapter-markdown-content.novel-reading-options .prose h4,
                    .chapter-markdown-content.novel-reading-options .prose h5,
                    .chapter-markdown-content.novel-reading-options .prose h6 { font-size: 1.05em !important; }
                    
                    .prose {
                        line-height: inherit;
                    }
                    
                    .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
                        color: #ffffff;
                        margin-top: 2rem;
                        margin-bottom: 1rem;
                        font-weight: 700;
                        line-height: 1.3;
                    }

                    .prose h1 { font-size: 2.25em; }
                    .prose h2 { font-size: 1.875em; }
                    .prose h3 { font-size: 1.5em; }
                    .prose h4 { font-size: 1.25em; }
                    .prose h5 { font-size: 1.125em; }
                    .prose h6 { font-size: 1em; }
                    
                    .prose p {
                        margin-bottom: 1.5rem;
                        text-align: justify;
                    }
                    
                    .prose a {
                        color: #60a5fa;
                        text-decoration: underline;
                    }
                    
                    .prose code {
                        background: #2d2d2d;
                        padding: 0.2rem 0.4rem;
                        border-radius: 4px;
                        font-size: 0.9em;
                    }
                    
                    .prose pre {
                        background: #2d2d2d;
                        padding: 1rem;
                        border-radius: 8px;
                        overflow-x: auto;
                    }
                    
                    .prose blockquote {
                        border-left: 4px solid #60a5fa;
                        padding-left: 1rem;
                        font-style: italic;
                        color: #b0b0b0;
                    }
                    
                    .prose img {
                        max-width: 100%;
                        height: auto;
                        border-radius: 8px;
                        margin: 1.5rem auto;
                        display: block;
                    }
                    
                    .prose ul, .prose ol {
                        margin-left: 1.5rem;
                        margin-bottom: 1.5rem;
                    }
                    
                    .prose li {
                        margin-bottom: 0.5rem;
                    }
                    
                    .prose table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 1.5rem 0;
                    }
                    
                    .prose th, .prose td {
                        border: 1px solid #404040;
                        padding: 0.75rem;
                        text-align: left;
                    }
                    
                    .prose th {
                        background: #2d2d2d;
                        font-weight: bold;
                    }
                ` }} />
            </div>
        );
    }

    if (tipo === 'texto') {
        return (
            <div
                className={`chapter-plain-text ${hasReadingOptions ? 'novel-reading-options' : ''}`}
                style={wrapperStyle}
            >
                <div className="max-w-none text-zinc-100/95" style={containerStyle}>
                    {conteudo}
                </div>
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
                    .chapter-plain-text .max-w-none {
                        white-space: pre-wrap;
                        word-break: break-word;
                        line-height: 1.75;
                        text-align: justify;
                    }
                    .chapter-plain-text.novel-reading-options .max-w-none {
                        font-size: inherit !important;
                        font-family: inherit !important;
                        line-height: inherit !important;
                    }
                `,
                    }}
                />
            </div>
        );
    }

    return null;
}
